/* Unified Three.js scene for all .glb balls.
   - Desktop: fixed-position canvas covering viewport, Lenis scroll sync, full PBR.
   - Mobile:  ABSOLUTE-positioned canvas inside the lab grid container, sized to
              match it. Canvas scrolls with the DOM via the browser's compositor —
              no JS scroll sync needed, no lag. JS only updates rotations. */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { toCreasedNormals } from 'three/addons/utils/BufferGeometryUtils.js';

(function () {
  const SLOT_SELECTOR     = '[data-ball-src]';
  const BALL_SCALE_FACTOR = 0.83;
  const ROLL_FACTOR       = -0.35;   // negative = roll forward (away from viewer) on scroll-down

  // Per-ball scale overrides — used for balls whose visible body is smaller
  // than their bounding box (e.g. Spike Ball with spikes that extend the box).
  function customScaleMultiplier(src) {
    if (!src) return 1;
    if (/Spike_Ball/i.test(src)) {
      return readCssVar('--ball-spike-boost', 1.15);
    }
    return 1;
  }
  function readCssVar(name, fallback) {
    const scope = document.querySelector('.lab-debug-scope');
    if (!scope) return fallback;
    const v = parseFloat(getComputedStyle(scope).getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }
  // Public hook so the debug panel slider can re-apply on change
  window.__rescaleSpecialBalls = null;

  // ─── Per-ball material overrides ──────────────────────────────────────────
  // Live tuning per ball (stored in localStorage), plus a hardcoded baseline
  // table for balls whose tuning has been baked in.
  //
  // Override shape: { color: '#hex'?, metalness: 0..1?, roughness: 0..1?,
  //                   envIntensity: 0..6?, killMap: true? }
  // Any field omitted = keep the GLB's original value for that property.
  const BALL_OVERRIDES_HARDCODED = {
    'Golden_Globe_Ball': {
      desktop: { color: '#d9a930', metalness: 0.80, roughness: 0.99, envIntensity: 1.00, killMap: true, creaseAngle: 30 },
      mobile:  { forcePBR: true },  // inherits all desktop values + uses PBR pipeline
    },
    'Trionda_Legacy_Ball': { color: '#d9a930', metalness: 1, roughness: 1, brightness: 0.1, aoIntensity: 1.65, killMap: true },
    '180_Triangle_Ball': { color: '#ee961b' },
    'Fish_Escher_Ball': { metalness: 0.84, roughness: 1, envIntensity: 0.5, bumpiness: 0.15, aoIntensity: 1 },
    '232_Panels_Ball': { metalness: 0, roughness: 1, envIntensity: 2.2, brightness: 0, bumpiness: 0.15, aoIntensity: 0 },
    '66_Panel_Globe_Ball': { bumpiness: 0, creaseAngle: 30 },
    'Burki_Ball': { metalness: 0, roughness: 1, envIntensity: 0.6, brightness: 0, aoIntensity: 0 },
    'Cube_Ball': { metalness: 0, roughness: 1, envIntensity: 0.4, brightness: 0, bumpiness: 0, aoIntensity: 0, creaseAngle: 30 },
    'England_Ball': { metalness: 0, roughness: 1, envIntensity: 1.4 },
    'Spiral_Ball': { metalness: 1, envIntensity: 1.3, brightness: 0, bumpiness: 0.05, aoIntensity: 0.45 },
    'Tetris_Ball': { creaseAngle: 30 },
    'The_Bernardes_Ball': { creaseAngle: 30 },
    'The_Brazucaca_Ball': { bumpiness: 1.45, creaseAngle: 30 },
    'Watermelon_Ball': { metalness: 0.06, roughness: 1, envIntensity: 6, bumpiness: 0.4, aoIntensity: 0.2, opacity: 1 },
    'Modelo_Ball': { metalness: 0.56, roughness: 1, envIntensity: 0, brightness: 0, aoIntensity: 0.5, opacity: 1 },
  };
  // No persistence: in-session only. Hard-coded BALL_OVERRIDES_HARDCODED is
  // the only source of truth shipped to visitors. Wipe any legacy entry.
  window.__ballOverridesLive = window.__ballOverridesLive || {};
  try { localStorage.removeItem('ballMaterialOverridesV1'); } catch (e) {}

  function ballKeyFromSrc(src) {
    if (!src) return '';
    return src.split(/[?#]/)[0].split('/').pop()
      .replace(/\.glb$/i, '').replace(/_mobile$/i, '');
  }
  function getBallOverride(key) {
    if (!key) return null;
    const platform = isMobile ? 'mobile' : 'desktop';
    function pickFor(entry, p) {
      if (!entry) return null;
      // Platform-aware shape — apply only this platform's overrides
      if (entry.desktop || entry.mobile) return entry[p] || null;
      // Legacy flat shape — applies to both platforms
      return entry;
    }
    let result = pickFor(window.__ballOverridesLive[key], platform)
              || pickFor(BALL_OVERRIDES_HARDCODED[key], platform);
    // Mobile defaults to forcePBR (render with the same PBR + HDR pipeline
    // as desktop so balls look the same). To opt a specific ball out and
    // keep the lighter Phong path, set its `mobile: { forcePBR: false }`
    // override.
    if (isMobile && (!result || result.forcePBR !== false)) {
      const desktopOv = pickFor(window.__ballOverridesLive[key], 'desktop')
                     || pickFor(BALL_OVERRIDES_HARDCODED[key], 'desktop')
                     || result
                     || {};
      result = Object.assign({}, desktopOv, { forcePBR: true });
    }
    return result;
  }

  // Tracks every material instance grouped by ball key, so the debug sliders
  // can re-apply tuning to all instances live without reload.
  const ballMaterials = new Map();   // key -> Set<material>

  function trackMaterial(key, material) {
    if (!key) return;
    if (!ballMaterials.has(key)) ballMaterials.set(key, new Set());
    ballMaterials.get(key).add(material);
    // Snapshot the original GLB values so we can restore them when the user
    // disables an override field (or toggles "Kill texture" off).
    if (!material.userData._origMaterial) {
      material.userData._origMaterial = {
        color: material.color ? material.color.getHex() : null,
        metalness: material.metalness,
        roughness: material.roughness,
        envMapIntensity: material.envMapIntensity,
        map: material.map || null,
        shininess: material.shininess,
        specular: material.specular ? material.specular.getHex() : null,
        emissive: material.emissive ? material.emissive.getHex() : 0,
        emissiveIntensity: (material.emissiveIntensity != null) ? material.emissiveIntensity : 1,
        normalScaleX: material.normalScale ? material.normalScale.x : 1,
        normalScaleY: material.normalScale ? material.normalScale.y : 1,
        aoMapIntensity: (material.aoMapIntensity != null) ? material.aoMapIntensity : 1,
        opacity: (material.opacity != null) ? material.opacity : 1,
        transparent: !!material.transparent,
      };
    }
  }

  // Each override field is OPTIONAL.  An undefined / missing field means
  // "use the GLB's original value for this property".  This lets the user
  // tune one property (e.g. roughness) without inadvertently locking in
  // the others.
  function applyOverrideToMaterial(material, override) {
    const orig = material.userData._origMaterial;
    if (!orig) return;
    const ov = override || {};

    if (material.isMeshStandardMaterial) {
      if (ov.color) material.color.set(ov.color);
      else if (orig.color != null) material.color.setHex(orig.color);

      material.metalness = (ov.metalness != null) ? ov.metalness : orig.metalness;
      material.roughness = (ov.roughness != null) ? ov.roughness : orig.roughness;
      material.envMapIntensity = (ov.envIntensity != null) ? ov.envIntensity : orig.envMapIntensity;
      material.map = ov.killMap ? null : orig.map;

      // Brightness via emissive — copies the diffuse color to emissive and
      // scales by the brightness value, so a ball lifts without changing tint.
      if (ov.brightness != null && ov.brightness > 0) {
        material.emissive.copy(material.color);
        material.emissiveIntensity = ov.brightness;
      } else {
        material.emissive.setHex(orig.emissive || 0);
        material.emissiveIntensity = orig.emissiveIntensity;
      }

      // Bumpiness — scale the normal map.  Vector2, both axes get the same value.
      const ns = (ov.bumpiness != null) ? ov.bumpiness : orig.normalScaleX;
      if (material.normalScale) material.normalScale.set(ns, (ov.bumpiness != null) ? ov.bumpiness : orig.normalScaleY);

      material.aoMapIntensity = (ov.aoIntensity != null) ? ov.aoIntensity : orig.aoMapIntensity;

      // Opacity — flip transparent flag automatically when < 1
      const op = (ov.opacity != null) ? ov.opacity : orig.opacity;
      material.opacity = op;
      material.transparent = (op < 1) ? true : orig.transparent;

      material.needsUpdate = true;
    } else if (material.isMeshPhongMaterial) {
      // Mobile: map the same override to Phong-equivalent properties.
      if (ov.color) material.color.set(ov.color);
      else if (orig.color != null) material.color.setHex(orig.color);

      if (ov.roughness != null) {
        material.shininess = Math.max(0, 100 * (1 - ov.roughness));
      } else if (orig.shininess != null) {
        material.shininess = orig.shininess;
      }

      if (ov.metalness != null) {
        const intensity = Math.round(0xff * ov.metalness);
        material.specular.setRGB(intensity / 255, (intensity * 0.88) / 255, (intensity * 0.5) / 255);
      } else if (orig.specular != null) {
        material.specular.setHex(orig.specular);
      }

      material.map = ov.killMap ? null : orig.map;

      if (ov.brightness != null && ov.brightness > 0) {
        material.emissive.copy(material.color);
        material.emissiveIntensity = ov.brightness;
      } else {
        material.emissive.setHex(orig.emissive || 0);
        material.emissiveIntensity = orig.emissiveIntensity;
      }
      const nsP = (ov.bumpiness != null) ? ov.bumpiness : orig.normalScaleX;
      if (material.normalScale) material.normalScale.set(nsP, (ov.bumpiness != null) ? ov.bumpiness : orig.normalScaleY);
      material.aoMapIntensity = (ov.aoIntensity != null) ? ov.aoIntensity : orig.aoMapIntensity;
      const opP = (ov.opacity != null) ? ov.opacity : orig.opacity;
      material.opacity = opP;
      material.transparent = (opP < 1) ? true : orig.transparent;

      material.needsUpdate = true;
    }
  }

  // Apply override to every material in the loaded model + register them
  // so live tuning can update them later.  Track FIRST so originals are
  // captured before the override mutates anything.
  // Also runs an optional crease-angle normal pass for balls whose source
  // export lacks hard edges at panel boundaries — splits vertices where
  // adjacent face angles exceed `creaseAngle` (degrees) so panels render
  // flat instead of as one continuous smoothed surface.
  function processLoadedModel(root, key) {
    const override = getBallOverride(key);
    const creaseDeg = override && override.creaseAngle;
    root.traverse((obj) => {
      if (!obj.isMesh) return;
      if (creaseDeg && obj.geometry) {
        try {
          obj.geometry = toCreasedNormals(obj.geometry, creaseDeg * Math.PI / 180);
        } catch (e) { console.warn('[balls-scene] crease pass failed for', key, e); }
      }
      if (!obj.material) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        trackMaterial(key, m);
        applyOverrideToMaterial(m, override);
      });
    });
  }

  // Public API for the debug panel
  window.__ballMaterialAPI = {
    listBalls() {
      // Return one entry per unique key, with a friendly title from the DOM
      const seen = new Set();
      const out = [];
      for (const s of slots) {
        const key = ballKeyFromSrc(s.src);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        let title = key;
        const card = s.el.closest && s.el.closest('a');
        const titleEl = card && card.querySelector('.lab-ball-name-n .marquee-inner');
        if (titleEl && titleEl.textContent.trim()) title = titleEl.textContent.trim();
        out.push({ key, src: s.src, title, hasMaterials: ballMaterials.has(key) });
      }
      return out;
    },
    getOverride(key) {
      return Object.assign({}, getBallOverride(key) || {});
    },
    // Returns the GLB's untouched values for the first tracked material of
    // this ball — the panel uses this to seed sliders so ticking the
    // "enable" checkbox doesn't change anything visually until the user
    // actually moves the slider.
    getOriginal(key) {
      const mats = ballMaterials.get(key);
      if (!mats || !mats.size) return null;
      for (const m of mats) {
        const o = m.userData._origMaterial;
        if (!o) continue;
        return {
          color: (o.color != null) ? '#' + o.color.toString(16).padStart(6, '0') : null,
          metalness: o.metalness,
          roughness: o.roughness,
          envIntensity: o.envMapIntensity,
          brightness: 0,                    // baseline = no extra brightness
          bumpiness: o.normalScaleX,
          aoIntensity: o.aoMapIntensity,
          opacity: o.opacity,
        };
      }
      return null;
    },
    setOverride(key, override) {
      if (!key) return;
      window.__ballOverridesLive[key] = override;
      const mats = ballMaterials.get(key);
      if (mats) mats.forEach((m) => applyOverrideToMaterial(m, override));
      needsRender = true;
    },
    clearOverride(key) {
      if (!key) return;
      delete window.__ballOverridesLive[key];
      // Re-apply hardcoded baseline (or leave as-is if none).  We can't
      // restore the GLB's original values without reloading, so live "reset"
      // falls back to hardcoded if present.
      const fallback = BALL_OVERRIDES_HARDCODED[key];
      const mats = ballMaterials.get(key);
      if (fallback && mats) mats.forEach((m) => applyOverrideToMaterial(m, fallback));
      needsRender = true;
    },
    exportHardcodeText() {
      const live = window.__ballOverridesLive || {};
      const keys = Object.keys(live).sort();
      if (!keys.length) return '';
      return keys.map((k) => {
        const o = live[k];
        const parts = [];
        if (o.color != null) parts.push("color: '" + o.color + "'");
        if (o.metalness != null) parts.push("metalness: " + o.metalness);
        if (o.roughness != null) parts.push("roughness: " + o.roughness);
        if (o.envIntensity != null) parts.push("envIntensity: " + o.envIntensity);
        if (o.brightness != null) parts.push("brightness: " + o.brightness);
        if (o.bumpiness != null) parts.push("bumpiness: " + o.bumpiness);
        if (o.aoIntensity != null) parts.push("aoIntensity: " + o.aoIntensity);
        if (o.opacity != null) parts.push("opacity: " + o.opacity);
        if (o.killMap) parts.push("killMap: true");
        if (o.forcePBR) parts.push("forcePBR: true");
        return "    '" + k + "': { " + parts.join(', ') + " },";
      }).join('\n');
    },
  };

  // ─── Per-ball rotation overrides ──────────────────────────────────────────
  // Sets the resting orientation of each ball (in degrees) before the
  // scroll-driven roll on the wrapper. Applied to the inner cloned mesh so
  // the per-frame wrapper.rotation = scrollArc never overwrites it.
  // Override shape: { x?: deg, y?: deg, z?: deg } — missing axis = 0.
  const BALL_ROTATION_HARDCODED = {
    'Burki_Ball': { y: 19, z: 93 },
    'Globe_Ball_4.0': { x: 70, y: 36 },
    'Impossible_Ball': { x: -1, y: 90 },
    'Impossible_Ball_Line_Variation': { y: 90 },
    'Playstation_Ball': { y: 180 },
    'The_Homing_Missile_Ball': { x: -31, y: 120, z: 180 },
    'Watermelon_Ball': { z: 32 },
    'Modelo_Ball': { x: 34, y: 42 },
    'Cube_Ball': { y: 14 },
    'Football_Manager_Ball': { x: -74, y: 60, z: -2 },
    'Tetris_Ball': { x: -18, y: 28 },
    '66_Panel_Globe_Ball': { x: 7, y: -17, z: 9 },
    'Burberry_Ball': { x: 2, y: -18 },
    'Glitch_Ball': { x: 44 },
    'Melting_Ball': { x: 48, y: 1, z: -1 },
    'Messi_Ball': { x: -39, y: 18, z: 25 },
    'Pepsi_Ball': { x: 59 },
  };
  // No persistence: in-session only. BALL_ROTATION_HARDCODED is the only
  // source of truth shipped to visitors. Wipe any legacy entry.
  window.__ballRotationOverridesLive = window.__ballRotationOverridesLive || {};
  try { localStorage.removeItem('ballRotationOverridesV1'); } catch (e) {}

  const DEG2RAD = Math.PI / 180;
  const ballRotationGroups = new Map(); // key -> Set<Object3D>

  function getBallRotation(key) {
    if (!key) return null;
    return window.__ballRotationOverridesLive[key] || BALL_ROTATION_HARDCODED[key] || null;
  }

  function applyRotationToGroup(group, rot) {
    if (!group) return;
    if (!rot) { group.rotation.set(0, 0, 0); return; }
    group.rotation.set((rot.x || 0) * DEG2RAD, (rot.y || 0) * DEG2RAD, (rot.z || 0) * DEG2RAD);
  }

  function trackRotationGroup(key, group) {
    if (!key || !group) return;
    if (!ballRotationGroups.has(key)) ballRotationGroups.set(key, new Set());
    ballRotationGroups.get(key).add(group);
    applyRotationToGroup(group, getBallRotation(key));
  }

  window.__ballRotationAPI = {
    getOverride(key) {
      const r = getBallRotation(key);
      return r ? Object.assign({ x: 0, y: 0, z: 0 }, r) : { x: 0, y: 0, z: 0 };
    },
    setOverride(key, rot) {
      if (!key) return;
      const clean = {
        x: parseFloat(rot && rot.x) || 0,
        y: parseFloat(rot && rot.y) || 0,
        z: parseFloat(rot && rot.z) || 0,
      };
      if (clean.x === 0 && clean.y === 0 && clean.z === 0) {
        delete window.__ballRotationOverridesLive[key];
      } else {
        window.__ballRotationOverridesLive[key] = clean;
      }
      const groups = ballRotationGroups.get(key);
      if (groups) groups.forEach((g) => applyRotationToGroup(g, getBallRotation(key)));
      needsRender = true;
    },
    clearOverride(key) {
      if (!key) return;
      delete window.__ballRotationOverridesLive[key];
      const fallback = BALL_ROTATION_HARDCODED[key] || null;
      const groups = ballRotationGroups.get(key);
      if (groups) groups.forEach((g) => applyRotationToGroup(g, fallback));
      needsRender = true;
    },
    exportHardcodeText() {
      const live = window.__ballRotationOverridesLive || {};
      const keys = Object.keys(live).sort();
      if (!keys.length) return '';
      return keys.map((k) => {
        const r = live[k];
        const parts = [];
        if (r.x) parts.push('x: ' + r.x);
        if (r.y) parts.push('y: ' + r.y);
        if (r.z) parts.push('z: ' + r.z);
        return "    '" + k + "': { " + parts.join(', ') + " },";
      }).join('\n');
    },
  };

  const PIXEL_RATIO_CAP_DESKTOP = 2;
  // 1.5 on mobile = 44% fewer pixels to shade vs 2.0, visually
  // indistinguishable on small phone screens.
  const PIXEL_RATIO_CAP_MOBILE  = 1.5;

  // ---- Adaptive desktop quality ------------------------------------------
  // Capable machines run untouched at the full cap above. Weak GPUs get the
  // pixel ratio dialled down so the scene stays smooth instead of lagging /
  // dropping models. Starts at full quality and only steps DOWN, driven by
  // measured frame time during scroll/hover, so a strong machine never trips
  // it. A conservative hardware hint lets obviously-weak machines start lower.
  const QUALITY_FLOOR = 0.75;          // never go below this pixel ratio
  let qualityCap = PIXEL_RATIO_CAP_DESKTOP;   // current pixel-ratio ceiling
  let qualityAA  = true;               // antialias (decided once, at init)
  let frameEMA = 0;                    // smoothed ms/frame during animation
  let lastFrameStamp = 0;
  let framesSinceStep = 0;

  let canvas, scene, camera, renderer;
  let mobileAmbient = null, mobileKey = null, mobileFill = null;
  let dracoLoader, gltfLoader;
  let envMap = null;
  let slots = [];
  let modelCache = new Map();
  let modelLoading = new Map();
  let isMobile = false;
  let lenis = null;

  // Mobile-specific (absolute canvas layout)
  let gridContainer = null;
  let canvasW = 0, canvasH = 0;

  // Desktop-specific
  let needsRectRefresh = true;

  // Dirty-frame rendering: only render when something actually changed.
  // Set true initially so the first frame paints.  Any event that affects
  // visuals (scroll, hover, mesh loaded, resize) marks it true again.
  let needsRender = true;
  let lastRenderedSY = -Infinity;

  console.log('[balls-scene] module loaded');

  function setup() {
    isMobile = window.innerWidth < 768;
    console.log('[balls-scene] isMobile:', isMobile);
    if (isMobile) setupMobile();
    else setupDesktop();
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  MOBILE: Canvas positioned absolutely over the lab grid container.
  //  Scrolls with DOM via compositor. JS only updates rotations.
  // ─────────────────────────────────────────────────────────────────────────

  function setupMobile() {
    gridContainer = document.querySelector('.blog-posts-holder.addpt-h')
                 || document.querySelector('.blog-posts-holder');
    if (!gridContainer) {
      console.warn('[balls-scene] No lab grid container found, bailing');
      return;
    }
    if (getComputedStyle(gridContainer).position === 'static') {
      gridContainer.style.position = 'relative';
    }

    const rect = gridContainer.getBoundingClientRect();
    canvasW = rect.width;
    canvasH = rect.height;

    canvas = document.createElement('canvas');
    canvas.id = 'balls-scene-canvas';
    canvas.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:5';
    gridContainer.appendChild(canvas);

    scene = new THREE.Scene();
    camera = new THREE.OrthographicCamera(
      -canvasW / 2, canvasW / 2, canvasH / 2, -canvasH / 2, -2000, 2000
    );
    camera.position.z = 1000;

    renderer = new THREE.WebGLRenderer({
      canvas, alpha: true, antialias: true, powerPreference: 'high-performance',
    });
    renderer.setSize(canvasW, canvasH, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, PIXEL_RATIO_CAP_MOBILE));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Match desktop's tonemapping so PBR balls don't blow out. Lights below
    // start cranked for the no-HDR Phong fallback; ensureMobileHDR drops
    // them once the env map loads, mirroring desktop's post-HDR levels.
    renderer.toneMapping = THREE.NeutralToneMapping || THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    mobileAmbient = new THREE.AmbientLight(0xffffff, 1.8);
    scene.add(mobileAmbient);
    mobileKey = new THREE.DirectionalLight(0xffffff, 1.0);
    mobileKey.position.set(0.4, 1, 0.8);
    scene.add(mobileKey);
    mobileFill = new THREE.DirectionalLight(0xffffff, 0.5);
    mobileFill.position.set(-0.5, 0.4, -0.5);
    scene.add(mobileFill);

    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);
    gltfLoader.setCrossOrigin('anonymous');

    discoverSlotsMobile();

    // Resize handling — update canvas + camera + slot positions
    const ro = new ResizeObserver(onMobileResize);
    ro.observe(gridContainer);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onMobileResize);
    }

    requestAnimationFrame(tickMobile);
    console.log('[balls-scene] mobile setup complete, slots:', slots.length);

    window.__rescaleSpecialBalls = rescaleSpecialMobileBalls;

    // Background-preload all balls so they're cached when scrolled into view.
    // Staggered to avoid spiking the network or main thread.
    schedulePreload(loadMeshMobile);
  }

  function onMobileResize() {
    const rect = gridContainer.getBoundingClientRect();
    canvasW = rect.width;
    canvasH = rect.height;
    camera.left = -canvasW / 2;
    camera.right = canvasW / 2;
    camera.top = canvasH / 2;
    camera.bottom = -canvasH / 2;
    camera.updateProjectionMatrix();
    renderer.setSize(canvasW, canvasH, false);
    refreshSlotsMobile();
    needsRender = true;
  }

  function discoverSlotsMobile() {
    const els = gridContainer.querySelectorAll(SLOT_SELECTOR);
    slots = [];
    for (const el of els) {
      slots.push({
        el,
        src: el.getAttribute('data-ball-src'),
        mesh: null,
        x: 0, y: 0,        // canvas-local coords
        docTop: 0,          // for visibility checks
        docBottom: 0,
        width: 0,
      });
    }
    refreshSlotsMobile();
  }

  function refreshSlotsMobile() {
    const containerRect = gridContainer.getBoundingClientRect();
    const containerDocTop = containerRect.top + window.scrollY;
    for (const s of slots) {
      const rect = s.el.getBoundingClientRect();
      const cellCenterX = rect.left - containerRect.left + rect.width / 2;
      const cellCenterY = rect.top - containerRect.top + rect.height / 2;
      s.x = cellCenterX;
      s.y = cellCenterY;
      s.width = rect.width;
      s.docTop = containerDocTop + rect.top - containerRect.top;
      s.docBottom = s.docTop + rect.height;

      // Position the mesh ONCE — never updated per scroll
      if (s.mesh) {
        s.mesh.position.x = s.x - canvasW / 2;
        s.mesh.position.y = canvasH / 2 - s.y;
        s.mesh.scale.setScalar(s.width * BALL_SCALE_FACTOR * customScaleMultiplier(s.src));
      }
    }
  }

  // Hook for the debug panel — re-applies the scale on the spike ball mesh
  // (and any other custom-scaled balls) when the slider changes.
  function rescaleSpecialMobileBalls() {
    for (const s of slots) {
      if (!s.mesh) continue;
      if (customScaleMultiplier(s.src) === 1) continue;
      s.mesh.scale.setScalar(s.width * BALL_SCALE_FACTOR * customScaleMultiplier(s.src));
    }
  }

  function attachToSlotMobile(slot, prototype) {
    const wrapper = new THREE.Group();
    const inner = prototype.clone();
    wrapper.add(inner);
    wrapper.position.x = slot.x - canvasW / 2;
    wrapper.position.y = canvasH / 2 - slot.y;
    wrapper.scale.setScalar(slot.width * BALL_SCALE_FACTOR * customScaleMultiplier(slot.src));
    prepareFadeIn(slot, inner);
    slot.mesh = wrapper;
    scene.add(wrapper);
    trackRotationGroup(ballKeyFromSrc(slot.src), inner);
    needsRender = true;
  }

  // Shared appear-animation helpers used by both mobile + desktop ticks.
  // Each ball does a subtle opacity fade from 0 -> 1 on its materials when
  // it first lands in the scene. No scale change, no pop — just a soft
  // fade. Materials are cloned per-ball so changing one ball's opacity
  // doesn't affect siblings that share the prototype.
  const BALL_APPEAR_MS = 360;
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function ballAppearT(slot, now) {
    if (!slot.appearedAt) return 1;
    const t = (now - slot.appearedAt) / BALL_APPEAR_MS;
    return t >= 1 ? 1 : t < 0 ? 0 : t;
  }
  function prepareFadeIn(slot, root) {
    /* Each rendered ball gets its own material clone so the fade-in
       transparency doesn't leak across instances. The clone is what the
       renderer actually displays, so we register it in ballMaterials and
       copy the prototype's _origMaterial snapshot across — otherwise the
       debug sliders update the prototype while the visible clone keeps
       the GLB defaults. */
    const key = ballKeyFromSrc(slot.src);
    const mats = [];
    function adoptClone(orig, clone) {
      clone.transparent = true;
      clone.opacity = 0;
      clone.userData._origMaterial = orig.userData._origMaterial;
      trackMaterial(key, clone);
      mats.push(clone);
    }
    root.traverse((child) => {
      if (!child.isMesh || !child.material) return;
      if (Array.isArray(child.material)) {
        child.material = child.material.map((m) => {
          const c = m.clone();
          adoptClone(m, c);
          return c;
        });
      } else {
        const orig = child.material;
        const c = orig.clone();
        child.material = c;
        adoptClone(orig, c);
      }
    });
    slot.fadeMaterials = mats;
    slot.appearedAt = performance.now();
    /* Re-apply the current override to the clones so the visible material
       starts at the override values (not the GLB defaults). */
    const ov = getBallOverride(key);
    if (ov) mats.forEach((m) => applyOverrideToMaterial(m, ov));
  }
  function tickFade(slot, now) {
    if (!slot.fadeMaterials) return;
    const t = ballAppearT(slot, now);
    const eased = easeOutCubic(t);
    for (let i = 0; i < slot.fadeMaterials.length; i++) {
      slot.fadeMaterials[i].opacity = eased;
    }
    if (t >= 1) {
      // Stop the per-frame tween + drop back to opaque rendering for perf.
      for (let i = 0; i < slot.fadeMaterials.length; i++) {
        const m = slot.fadeMaterials[i];
        m.opacity = 1;
        m.transparent = false;
        m.needsUpdate = true;
      }
      slot.fadeMaterials = null;
    }
  }

  function tickMobile() {
    const sy = window.scrollY;
    const scrollChanged = Math.abs(sy - lastRenderedSY) > 0.1;
    // Track whether any ball is mid-appear so we keep rendering frames
    // even when the user isn't scrolling.
    let anyAppearing = false;
    const now = performance.now();
    for (let i = 0; i < slots.length; i++) {
      if (slots[i].mesh && ballAppearT(slots[i], now) < 1) { anyAppearing = true; break; }
    }

    if (scrollChanged || needsRender || anyAppearing) {
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const visTop = sy - 300;
      const visBottom = sy + vh + 300;
      const scrollArc = sy * ROLL_FACTOR;

      for (let i = 0; i < slots.length; i++) {
        const s = slots[i];
        const visible = (s.docBottom > visTop) && (s.docTop < visBottom);

        if (!s.mesh) {
          if (visible) loadMeshMobile(s);
          continue;
        }

        s.mesh.visible = visible;
        if (!visible) continue;

        const radius = (s.width * BALL_SCALE_FACTOR) / 2;
        s.mesh.rotation.x = scrollArc / radius;
        // Subtle opacity fade-in per ball — no scale change.
        tickFade(s, now);
      }

      renderer.render(scene, camera);
      lastRenderedSY = sy;
      needsRender = anyAppearing;
    }
    requestAnimationFrame(tickMobile);
  }

  // Lazy-load HDR env on the mobile renderer so any forcePBR ball gets
  // proper reflections.  No-op for Phong materials (they ignore env map).
  let mobileEnvLoaded = false;
  function ensureMobileHDR() {
    if (mobileEnvLoaded || !renderer) return;
    mobileEnvLoaded = true;
    new RGBELoader().load(resolveHdrUrl(),
      (texture) => {
        try {
          const pmrem = new THREE.PMREMGenerator(renderer);
          scene.environment = pmrem.fromEquirectangular(texture).texture;
          if ('environmentIntensity' in scene) scene.environmentIntensity = 1.0;
          texture.dispose();
          pmrem.dispose();
          // HDR is providing ambient — drop the manual lights so they don't
          // double-illuminate. Mirrors desktop's post-HDR levels.
          if (mobileAmbient) mobileAmbient.intensity = 0.15;
          if (mobileKey) mobileKey.intensity = 0.1;
          if (mobileFill) mobileFill.intensity = 0;
          needsRender = true;
        } catch (e) { console.warn('[balls-scene] mobile HDR setup failed', e); }
      },
      undefined,
      (err) => { console.warn('[balls-scene] mobile HDR load failed', err); }
    );
  }

  function loadMeshMobile(slot) {
    if (!slot.src || slot.mesh) return;
    const key = ballKeyFromSrc(slot.src);
    const override = getBallOverride(key);
    const forcePBR = !!(override && override.forcePBR);
    // forcePBR balls keep the full-res desktop GLB; Phong balls use _mobile.glb
    const src = forcePBR
      ? slot.src
      : (/\/assets\/[^/]+\.glb/i.test(slot.src) ? slot.src.replace(/\.glb$/i, '_mobile.glb') : slot.src);

    const cached = modelCache.get(src);
    if (cached) { attachToSlotMobile(slot, cached); return; }

    if (modelLoading.has(src)) {
      modelLoading.get(src).then(() => {
        if (!slot.mesh && modelCache.has(src)) attachToSlotMobile(slot, modelCache.get(src));
      });
      return;
    }

    const promise = new Promise((resolve) => {
      gltfLoader.load(src,
        (gltf) => {
          const root = gltf.scene;
          if (!forcePBR) downgradeMaterials(root);
          else ensureMobileHDR();
          processLoadedModel(root, key);
          const box = new THREE.Box3().setFromObject(root);
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z) || 1;
          const center = box.getCenter(new THREE.Vector3());
          root.position.sub(center);
          root.scale.setScalar(1 / maxDim);
          modelCache.set(src, root);
          if (!slot.mesh) attachToSlotMobile(slot, root);
          resolve();
        },
        undefined,
        (err) => {
          console.warn('[balls-scene] Failed', src, err);
          const card = slot.el.closest('a');
          if (card) card.style.display = 'none';
          resolve();
        }
      );
    });
    modelLoading.set(src, promise);
    promise.finally(() => modelLoading.delete(src));
  }

  function downgradeMaterials(root) {
    root.traverse((obj) => {
      if (!obj.isMesh || !obj.material) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      const replaced = mats.map((m) => {
        if (m.isMeshPhongMaterial || m.isMeshBasicMaterial) return m;
        // Phong gives subtle specular highlights — looks more like real leather
        // than flat Lambert, with negligible perf cost vs Lambert.  Per-ball
        // material overrides are applied later via processLoadedModel().
        const newMat = new THREE.MeshPhongMaterial({
          map: m.map || null,
          normalMap: m.normalMap || null,
          color: m.color ? m.color.clone() : new THREE.Color(0xffffff),
          transparent: !!m.transparent,
          opacity: m.opacity != null ? m.opacity : 1,
          side: m.side != null ? m.side : THREE.FrontSide,
          shininess: 22,
          specular: new THREE.Color(0x222222),
        });
        if (newMat.map) newMat.map.colorSpace = THREE.SRGBColorSpace;
        return newMat;
      });
      obj.material = Array.isArray(obj.material) ? replaced : replaced[0];
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  DESKTOP: Fixed canvas + Lenis scroll sync + full PBR
  // ─────────────────────────────────────────────────────────────────────────

  // Pick a conservative starting quality. When in any doubt, full quality —
  // only machines that clearly look weak begin degraded. deviceMemory is
  // undefined on Safari/Firefox, so those default to "capable".
  function initQualityHeuristic() {
    qualityCap = PIXEL_RATIO_CAP_DESKTOP;
    qualityAA  = true;
    try {
      const cores = navigator.hardwareConcurrency || 8;
      const mem   = navigator.deviceMemory || 8;
      if (mem <= 4 && cores <= 4) { qualityCap = 1.0; qualityAA = false; }
    } catch (e) {}
  }

  function applyQualityCap() {
    if (!renderer) return;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, qualityCap));
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    needsRender = true;
  }

  // Called once per actually-rendered desktop frame. Watches frame time during
  // continuous animation (scroll/hover) and steps the pixel ratio down if the
  // GPU can't keep up. Down-only within a session, so it can't oscillate.
  function adaptQuality(time) {
    if (qualityCap <= QUALITY_FLOOR) { lastFrameStamp = time; return; }
    const gap = time - lastFrameStamp;
    if (lastFrameStamp && gap < 100) {
      frameEMA = frameEMA ? (frameEMA * 0.9 + gap * 0.1) : gap;
      framesSinceStep++;
      // ~40 continuous frames averaging worse than ~22fps => step down
      if (framesSinceStep > 40 && frameEMA > 45) {
        qualityCap = Math.max(QUALITY_FLOOR, qualityCap - 0.25);
        applyQualityCap();
        frameEMA = 0; framesSinceStep = 0;
        if (window.console) console.log('[balls-scene] quality stepped down to pixelRatio cap', qualityCap);
      }
    } else {
      // animation restarted after an idle gap — reset the measurement window
      frameEMA = 0; framesSinceStep = 0;
    }
    lastFrameStamp = time;
  }

  function setupDesktop() {
    canvas = document.createElement('canvas');
    canvas.id = 'balls-scene-canvas';
    canvas.style.cssText =
      'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:5;will-change:transform';
    document.body.prepend(canvas);

    scene = new THREE.Scene();
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera = new THREE.OrthographicCamera(-w / 2, w / 2, h / 2, -h / 2, -2000, 2000);
    camera.position.z = 1000;

    initQualityHeuristic();
    renderer = new THREE.WebGLRenderer({
      canvas, alpha: true, antialias: qualityAA, powerPreference: 'high-performance',
    });
    renderer.setSize(w, h, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, qualityCap));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NeutralToneMapping || THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 0.3);
    key.position.set(0.5, 1, 0.8);
    scene.add(key);

    // HDR env map for proper PBR IBL
    const hdrUrl = resolveHdrUrl();
    new RGBELoader().load(hdrUrl,
      (texture) => {
        try {
          const pmrem = new THREE.PMREMGenerator(renderer);
          envMap = pmrem.fromEquirectangular(texture).texture;
          scene.environment = envMap;
          if ('environmentIntensity' in scene) scene.environmentIntensity = 1.0;
          texture.dispose();
          pmrem.dispose();
          ambient.intensity = 0.15;
          key.intensity = 0.1;
        } catch (e) { console.warn('HDR setup failed', e); }
      },
      undefined,
      (err) => { console.warn('HDR load failed', err); }
    );

    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);
    gltfLoader.setCrossOrigin('anonymous');

    initLenis();
    discoverSlotsDesktop();
    window.addEventListener('resize', onDesktopResize);
    requestAnimationFrame(tickDesktop);

    // Background-preload all balls so they're cached when scrolled into view
    schedulePreload(loadMeshDesktop);
  }

  function schedulePreload(loadFn) {
    // Background preload — only when the main thread is idle, so we never
    // compete with active rendering or scrolling.  Browser HTTP cache + our
    // modelCache make later visibility-triggered hits effectively instant.
    if (!slots.length) return;
    let idx = 0;
    const ric = window.requestIdleCallback || function (cb) { return setTimeout(function () { cb({ timeRemaining: function () { return 50; } }); }, 100); };

    function preloadNext() {
      // Skip already-loaded / in-flight slots
      while (idx < slots.length) {
        const slot = slots[idx];
        if (!slot.mesh && !modelLoading.has(slot.src)) break;
        idx++;
      }
      if (idx >= slots.length) return;
      loadFn(slots[idx++]);
      if (idx < slots.length) {
        ric(preloadNext, { timeout: 1500 });
      }
    }
    // Wait a beat so visible-trigger loads start first, then trickle the rest
    setTimeout(function () { ric(preloadNext, { timeout: 1500 }); }, 600);
  }

  function resolveHdrUrl() {
    const scripts = document.querySelectorAll('script[src*="balls-scene"]');
    for (const s of scripts) {
      const m = s.src.match(/^(.*\/assets\/)/);
      if (m) return m[1] + 'neutral.hdr';
    }
    return '/assets/neutral.hdr';
  }

  async function initLenis() {
    try {
      const mod = await import('lenis');
      const LenisClass = mod.default || mod.Lenis;
      // lerp 1.0 = no smoothing, native scroll feel.  Do NOT change this
      // back to a fractional value — user explicitly wants native, and any
      // smoothing reads as "floaty/janky" to them.
      lenis = new LenisClass({ lerp: 1.0, smoothWheel: true, wheelMultiplier: 1.0, syncTouch: false });
    } catch (e) { lenis = null; }
  }

  function discoverSlotsDesktop() {
    const els = document.querySelectorAll(SLOT_SELECTOR);
    slots = [];
    const canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    for (const el of els) {
      const slot = {
        el, src: el.getAttribute('data-ball-src'), mesh: null,
        docX: 0, docY: 0, width: 0, height: 0,
        hover: false, hoverT: 0, hoverYaw: 0,
      };
      if (canHover) {
        // Circular hover hit-test — only count as "hovering" when the cursor
        // is within the visible ball circle, not anywhere in the rectangular
        // cell. Ball radius = cell_width * BALL_SCALE_FACTOR / 2.
        el.addEventListener('mousemove', (e) => {
          const rect = el.getBoundingClientRect();
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const r = (rect.width * BALL_SCALE_FACTOR) / 2;
          const dx = e.clientX - cx, dy = e.clientY - cy;
          const inside = (dx * dx + dy * dy) <= (r * r);
          if (inside !== slot.hover) {
            slot.hover = inside;
            needsRender = true;
          }
          // Cursor only shows pointer when over the ball
          el.style.cursor = inside ? 'pointer' : 'default';
        });
        el.addEventListener('mouseleave', () => {
          if (slot.hover) { slot.hover = false; needsRender = true; }
          el.style.cursor = '';
        });
      }

      // Click hit-test: prevent the link from navigating (and stop the lab
      // debug panel from selecting) when the click is outside the visible
      // ball circle. Capture-phase so we run before the link's default
      // and before the document-level debug-panel handler.
      const linkEl = el.closest('a');
      if (linkEl) {
        linkEl.addEventListener('click', (e) => {
          const rect = el.getBoundingClientRect();
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const r = (rect.width * BALL_SCALE_FACTOR) / 2;
          const dx = e.clientX - cx, dy = e.clientY - cy;
          if ((dx * dx + dy * dy) > r * r) {
            e.preventDefault();
            e.stopPropagation();
          }
        }, true);
      }
      slots.push(slot);
    }
    refreshRectsDesktop();
  }

  function refreshRectsDesktop() {
    const sy = window.scrollY;
    for (const s of slots) {
      const rect = s.el.getBoundingClientRect();
      s.docX = rect.left + rect.width / 2;
      s.docY = rect.top + sy + rect.height / 2;
      s.width = rect.width;
      s.height = rect.height;
    }
  }

  function attachDesktopMesh(slot, prototype) {
    const wrapper = new THREE.Group();
    // YXZ order: hover yaw (Y) is applied first in world space so it always
    // spins around vertical, regardless of how much X-roll the scroll has
    // accumulated. Default XYZ would make the hover spin around the tilted
    // local Y, which reads as a flat 2D flip after any scroll.
    wrapper.rotation.order = 'YXZ';
    const inner = prototype.clone();
    wrapper.add(inner);
    // Subtle material-opacity fade on first render — no scale animation.
    prepareFadeIn(slot, inner);
    slot.mesh = wrapper;
    scene.add(wrapper);
    trackRotationGroup(ballKeyFromSrc(slot.src), inner);
    needsRender = true;
  }

  function loadMeshDesktop(slot) {
    if (!slot.src || slot.mesh) return;
    const src = slot.src;
    const cached = modelCache.get(src);
    if (cached) { attachDesktopMesh(slot, cached); return; }
    if (modelLoading.has(src)) {
      modelLoading.get(src).then(() => {
        if (!slot.mesh && modelCache.has(src)) attachDesktopMesh(slot, modelCache.get(src));
      });
      return;
    }
    const promise = new Promise((resolve) => {
      gltfLoader.load(src,
        (gltf) => {
          const root = gltf.scene;
          processLoadedModel(root, ballKeyFromSrc(src));
          const box = new THREE.Box3().setFromObject(root);
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z) || 1;
          const center = box.getCenter(new THREE.Vector3());
          root.position.sub(center);
          root.scale.setScalar(1 / maxDim);
          modelCache.set(src, root);
          if (!slot.mesh) attachDesktopMesh(slot, root);
          resolve();
        },
        undefined,
        (err) => { console.warn('Failed', src, err); resolve(); }
      );
    });
    modelLoading.set(src, promise);
    promise.finally(() => modelLoading.delete(src));
  }

  function onDesktopResize() {
    const w = window.innerWidth, h = window.innerHeight;
    camera.left = -w / 2; camera.right = w / 2;
    camera.top = h / 2; camera.bottom = -h / 2;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
    needsRectRefresh = true;
    needsRender = true;
  }

  function tickDesktop(time) {
    if (lenis) { try { lenis.raf(time); } catch (e) {} }

    const sy = lenis ? (lenis.scroll || 0) : (window.scrollY || 0);
    const scrollChanged = Math.abs(sy - lastRenderedSY) > 0.1;

    // Hover-driven animation needs continuous frames while in flight.
    // Cheap loop — just property reads on a few dozen objects.
    let hoverActive = false;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (s.hover) { hoverActive = true; break; }
      if (s.hoverT > 0.005) { hoverActive = true; break; }
      if (s.hoverYaw !== 0) {
        const TWO_PI = Math.PI * 2;
        const tgt = Math.round(s.hoverYaw / TWO_PI) * TWO_PI;
        if (Math.abs(s.hoverYaw - tgt) > 0.005) { hoverActive = true; break; }
      }
    }

    if (!(scrollChanged || hoverActive || needsRender)) {
      requestAnimationFrame(tickDesktop);
      return;
    }

    if (needsRectRefresh) { refreshRectsDesktop(); needsRectRefresh = false; }

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const scrollArc = sy * ROLL_FACTOR;
    const VISIBLE_MARGIN = 500;

    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      const screenY = s.docY - sy;
      const visible = (screenY > -VISIBLE_MARGIN) && (screenY < vh + VISIBLE_MARGIN);

      if (!s.mesh) { if (visible) loadMeshDesktop(s); continue; }

      s.mesh.visible = visible;
      if (!visible) continue;

      s.mesh.position.x = s.docX - vw / 2;
      s.mesh.position.y = vh / 2 - screenY;

      // Hover tween (desktop only — mobile slots never set s.hover = true)
      const hoverTarget = s.hover ? 1 : 0;
      s.hoverT += (hoverTarget - s.hoverT) * 0.18;

      const baseSize = s.width * BALL_SCALE_FACTOR * customScaleMultiplier(s.src);
      s.mesh.scale.setScalar(baseSize * (1 + 0.08 * s.hoverT));
      // Subtle material-opacity fade — does not touch scale.
      if (s.fadeMaterials) { tickFade(s, time); needsRender = true; }

      // Roll uses the un-boosted radius so the angular speed stays consistent
      const radius = (s.width * BALL_SCALE_FACTOR) / 2;
      s.mesh.rotation.x = scrollArc / radius;

      // Hover spin on Y — doesn't touch X (the roll axis)
      if (s.hover) {
        s.hoverYaw += 0.022;
      } else if (Math.abs(s.hoverYaw) > 0.001) {
        const TWO_PI = Math.PI * 2;
        const target = Math.round(s.hoverYaw / TWO_PI) * TWO_PI;
        s.hoverYaw += (target - s.hoverYaw) * 0.12;
      }
      s.mesh.rotation.y = s.hoverYaw;
    }

    renderer.render(scene, camera);
    adaptQuality(time);
    lastRenderedSY = sy;
    needsRender = false;
    requestAnimationFrame(tickDesktop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(setup, 0));
  } else {
    setTimeout(setup, 0);
  }
})();
